import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ListChecks, Users, GraduationCap } from 'lucide-react';
import QuestionPreviewModal from '../../../components/VueQuestion/QuestionPreviewModal';
import EvalDetail from '../../../components/VueEvaluation/EvalDetail';
import { useSessionQCM } from '../../../hooks/fetch_api_hook/useSessionQCM';
import { usePostApi } from '../../../hooks/post_api_hook/usePostApi';
import { resolveBonneReponse } from '../../../utils/questionUtils';
import { url } from '../../../utils/confserveur';
import ResultatsHeader from './ResultatsHeader';
import ResultatsExerciceByEleve from './ResultatsExerciceByEleve';
import ResultatsExerciceByQuestion from './ResultatsExerciceByQuestion';

// ─── Constantes partagées (exportées pour les sous-composants) ───

// Onglets de base ; « Suivi par compétence » est ajouté dynamiquement quand la
// session est notée (cf. tabs dans le composant).
const TABS = [
  { key: 'eleves', label: 'Par élève', icon: Users },
  { key: 'questions', label: 'Par question', icon: ListChecks },
];

/** Initiales d'un nom (hoisted) */
export const getInitials = (nom) => {
  const parts = nom.split(' ');
  return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : nom.substring(0, 2);
};

/** Couleur du score selon le ratio */
export const getScoreColor = (score, total) => {
  if (score === total) return 'text-emerald-600';
  if (score > 0) return 'text-amber-600';
  return 'text-red-500';
};

/** Style carte — gradient aligné sur StudentCard */
export const CARD_BG = "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)";

// ─── Composant principal ───

const ResultatsExercice = () => {
  const { idclasse, id } = useParams();
  const [refreshCount, setRefreshCount] = useState(0);
  const { data: session, isLoading, isError } = useSessionQCM(id, refreshCount);
  const [activeTab, setActiveTab] = useState('eleves');
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [isResetting, setIsResetting] = useState(null);

  // Hook POST pour reset élève (sessions en ligne uniquement)
  const [, doResetPost] = usePostApi(`${url}eleves_sessions_qcm/submit_responses/`);

  const isEquipe = session?.mode === 'equipe';
  const isEnLigne = session?.mode_passation === 'en_ligne';

  // Session notée → segment "Suivi par compétence" : on monte EvalDetail (la
  // matrice de l'éval de suivi liée) en mode embedded. EvalDetail fetch sa
  // propre matrice via selectedEvalId ; selectedEval n'alimente que le récap.
  // Tant que la séance n'est pas close, la grille est en lecture seule : la
  // clôture (end/) note les copies et écraserait une correction à la main
  // (règle back/prof 15/09). Dès que le statut passe à terminee (relecture de
  // la session, bouton Actualiser du bandeau), la grille redevient éditable
  // sans rechargement de page.
  const seanceEnCours = !!session && session.statut !== 'terminee';
  const actualiserSession = useCallback(() => setRefreshCount(prev => prev + 1), []);
  const suiviEval = useMemo(() => (
    session?.evaluation
      ? {
          id: session.evaluation,
          titre: `QCM - ${session.titre || ''}`,
          date: session.date_passation || (session.date_fin ? session.date_fin.slice(0, 10) : ''),
          statut: 'terminee', // l'éval issue de la notation est verrouillée par défaut (Déverrouiller pour corriger)
          nombre_eleves: 0,
          moyenne_scores: null,
        }
      : null
  ), [session?.evaluation, session?.titre, session?.date_passation, session?.date_fin]);

  // Onglets : + "Suivi par compétence" si la session est notée
  const tabs = useMemo(
    () => (suiviEval ? [...TABS, { key: 'suivi', label: 'Suivi par compétence', icon: GraduationCap }] : TABS),
    [suiviEval]
  );

  // Reset les réponses d'un élève puis recharger la session
  const handleResetEleve = useCallback(async (eleve) => {
    setIsResetting(eleve.id_eleve);
    const resetPayload = {
      session_id: session?.id,
      eleve_id: eleve.id_eleve,
      classe_id: session?.classe,
      reponses: {}
    };
    await doResetPost(resetPayload);
    setRefreshCount(prev => prev + 1);
    setIsResetting(null);
  }, [session?.id, session?.classe, doResetPost]);

  // Questions de la session (avec banque_question)
  const questions = useMemo(() => {
    if (!session?.questions?.length) return [];
    return session.questions.map(sq => {
      const bq = sq.banque_question || {};
      return {
        question_ordre: sq.ordre,
        texte: bq.texte || '',
        bonneReponse: resolveBonneReponse(bq.contenu),
        type: bq.type || 'qcm',
        subType: bq.contenu?.subType,
        contenu: bq.contenu,
        choix: bq.contenu?.choix || [],
        elements_additionnels: bq.elements_additionnels,
        attendu_titre: bq.attendu_titre,
        savoir_faire_titre: bq.savoir_faire_titre,
        banque_question_id: bq.id,
      };
    });
  }, [session?.questions]);

  // Stats dérivées depuis session.eleves_reponses
  const { statsParQuestion, statsParEleve, elevesNonRepondus } = useMemo(() => {
    if (!questions.length) return { statsParQuestion: [], statsParEleve: [], elevesNonRepondus: [] };

    const eleveStatsMap = new Map();

    const statsParQuestion = questions.map((q) => {
      let bonnes = 0, total = 0;
      const repondants = [];

      (session?.eleves_reponses || []).filter(e => e.reponses).forEach(eleve => {
        const reponses = eleve.reponses || {};
        const qId = String(q.banque_question_id);
        let rep = null;

        // Format flat (ArUco) : reponses = { "8": {...} }
        // Format groupé (en ligne) : reponses = { "catégorie": { "36": {...} } }
        if (reponses[qId] && (reponses[qId].réponse !== undefined || reponses[qId].est_correcte !== undefined)) {
          rep = reponses[qId];
        } else {
          Object.values(reponses).forEach(catQuestions => {
            if (catQuestions && typeof catQuestions === 'object' && catQuestions[qId]) {
              rep = catQuestions[qId];
            }
          });
        }

        if (rep) {
          const correcte = rep.score > 0 || rep.est_correcte === true;
          total++;
          if (correcte) bonnes++;
          repondants.push({ nom: eleve.nom_eleve, reponse: rep.réponse || '', correcte });

          if (!eleveStatsMap.has(eleve.nom_eleve)) eleveStatsMap.set(eleve.nom_eleve, { id_eleve: eleve.id_eleve, reponses: [] });
          // `fiche` : la réponse telle que la copie l'a gardée, avec ses réglages de
          // correction (tolérance…) pour que le détail juge comme le score.
          eleveStatsMap.get(eleve.nom_eleve).reponses.push({ question: q.question_ordre, reponse: rep.réponse || '', correcte, fiche: rep });
        }
      });

      return { ...q, bonnes, total, taux: total > 0 ? Math.round((bonnes / total) * 100) : 0, repondants };
    });

    const statsParEleve = [...eleveStatsMap.entries()]
      .map(([nom, data]) => ({
        nom, id_eleve: data.id_eleve, reponses: data.reponses,
        score: data.reponses.filter(r => r.correcte).length, total: questions.length,
      }))
      .sort((a, b) => a.score - b.score || a.nom.localeCompare(b.nom));

    const elevesNonRepondus = (session?.eleves_reponses || [])
      .filter(e => !e.reponses)
      .map(e => ({ nom: e.nom_eleve, id_eleve: e.id_eleve, reponses: [], score: 0, total: questions.length, nonRepondu: true }))
      .sort((a, b) => a.nom.localeCompare(b.nom));

    return { statsParQuestion, statsParEleve, elevesNonRepondus };
  }, [questions, session?.eleves_reponses]);

  // Taux global : priorité au backend, fallback calculé
  const tauxGlobal = useMemo(() => {
    if (session?.taux_reussite != null) return Math.round(session.taux_reussite);
    const totalBonnes = statsParQuestion.reduce((acc, q) => acc + q.bonnes, 0);
    const totalReponses = statsParQuestion.reduce((acc, q) => acc + q.total, 0);
    return totalReponses > 0 ? Math.round((totalBonnes / totalReponses) * 100) : 0;
  }, [session?.taux_reussite, statsParQuestion]);

  // Callback stable pour setPreviewQuestion (passé aux enfants)
  const handlePreview = useCallback((q) => setPreviewQuestion(q), []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Chargement des résultats…</p>
      </div>
    );
  }

  if (isError || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">Impossible de charger les résultats.</p>
      </div>
    );
  }

  return (
    <div className="px-4 mt-5 max-w-full">
      <div>
        {/* Header gradient */}
        <ResultatsHeader
          session={session}
          statsParQuestion={statsParQuestion}
          statsParEleve={statsParEleve}
          elevesNonRepondus={elevesNonRepondus}
          tauxGlobal={tauxGlobal}
          idclasse={idclasse}
          isEquipe={isEquipe}
        />

        {/* Contenu */}
        <div className="rounded-b-2xl p-6 space-y-5">
          {/* Onglets */}
          <div className="flex p-1 rounded-2xl bg-white/40 backdrop-blur border border-white/60 shadow-md shadow-blue-900/5">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  activeTab === key
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-600/30'
                    : 'text-gray-500 hover:text-blue-700 hover:bg-white/60'
                }`}
              >
                <Icon size={15} />
                {key === 'eleves' && isEquipe ? 'Par équipe' : label}
              </button>
            ))}
          </div>

          {/* Vue par question */}
          {activeTab === 'questions' && (
            <ResultatsExerciceByQuestion
              statsParQuestion={statsParQuestion}
              setPreviewQuestion={handlePreview}
            />
          )}

          {/* Vue par élève */}
          {activeTab === 'eleves' && (
            <ResultatsExerciceByEleve
              statsParEleve={statsParEleve}
              elevesNonRepondus={elevesNonRepondus}
              questions={questions}
              isEnLigne={isEnLigne}
              isEquipe={isEquipe}
              handleResetEleve={handleResetEleve}
              isResetting={isResetting}
              setPreviewQuestion={handlePreview}
            />
          )}

          {/* Vue suivi par compétence (session notée) : matrice EvalDetail,
              éditable seulement une fois la séance close */}
          {activeTab === 'suivi' && suiviEval && (
            <EvalDetail
              selectedEval={suiviEval}
              selectedEvalId={suiviEval.id}
              setSelectedEvalId={() => {}}
              embedded
              lectureSeule={seanceEnCours}
              motifLectureSeule="Séance en cours : les corrections à la main se font après la clôture."
              onActualiser={actualiserSession}
            />
          )}
        </div>
      </div>

      {/* Preview question modal */}
      {previewQuestion && (
        <QuestionPreviewModal
          question={{
            texte: previewQuestion.texte,
            type_question: previewQuestion.type || 'qcm',
            contenu: previewQuestion.contenu || { choix: previewQuestion.choix, bonne_reponse: previewQuestion.bonneReponse },
            elements_additionnels: previewQuestion.elements_additionnels,
          }}
          onClose={() => setPreviewQuestion(null)}
        />
      )}
    </div>
  );
};

export default ResultatsExercice;
